import { Fragment, type ReactNode } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import type { RouteEntry } from './routes';
import { WORKSPACE_PARAM } from './workspace-path';

/**
 * ┌──────────────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ 3 Nov 2026 · A DIFFERENT RECORD IN THE ADDRESS REMOUNTS ITS SCREEN.          │
 * └──────────────────────────────────────────────────────────────────────────────────┘
 *
 * `app.tsx` once put `key={route.path}` on the `<Route>`, which React Router never uses as a React
 * key — and `<Routes>` renders the matched element at the same position whichever route matched.
 * So `/projects/A` → `/projects/B` kept the project screen's state (its overview tab drew A's
 * contract under B's header), and `/reports/:id` → `/reports/new` kept the builder's (the sidebar's
 * *+* opened the old report, and Mentés saved a copy of it).
 *
 * The key is the route plus the params it matched — bar `:workspace` — so the same record keeps its
 * state through a tab change (the hash) or a filter (the query) and a different one starts clean.
 * `RouteEntry.keyedBySearch` adds the query where it names the record; `keepsStateAcrossParams`
 * opts a route out, with the reason written down.
 */
export function routeElementKey(
  route: Pick<RouteEntry, 'path' | 'keyedBySearch' | 'keepsStateAcrossParams'>,
  params: Readonly<Record<string, string | undefined>>,
  search: string,
): string | undefined {
  if (route.keepsStateAcrossParams !== undefined) return undefined;
  const named = Object.keys(params)
    .filter((name) => name !== WORKSPACE_PARAM)
    .sort()
    .map((name) => `${name}=${params[name] ?? ''}`)
    .join('&');
  return `${route.path}|${named}${route.keyedBySearch === true ? `|${search}` : ''}`;
}

/** Renders `children` under the key `routeElementKey` derives from the current match. */
export function KeyedByRecord({
  route,
  children,
}: {
  readonly route: Pick<RouteEntry, 'path' | 'keyedBySearch' | 'keepsStateAcrossParams'>;
  readonly children: ReactNode;
}) {
  const params = useParams();
  const { search } = useLocation();
  return <Fragment key={routeElementKey(route, params, search)}>{children}</Fragment>;
}
